import { useNavigate } from "react-router-dom";
import { useEvent } from "../context/EventProvider";
import { withContext } from "../routes/registry";
import { useSettings } from "../settings/context";
import { useAnswers, type Answers } from "./answers";
import { stamped } from "./answerSession";
import { useSessionKey } from "../auth/useSessionKey";
import { useLeaveGuard } from "./leaveGuard";
import { activeSteps, stepAfter } from "./thread";
import { useOffer } from "./useOffre";
import type { Screen } from "../routes/registry";

/**
 * SE DÉPLACER DANS LE TUNNEL — l'étape d'avant, celle d'après, et le geste qui y va.
 *
 * ⚠️ SEPT ÉCRANS ÉCRIVAIENT CES CINQ LIGNES À L'IDENTIQUE, et c'est précisément le genre
 * de recopie que ce fil ne supporte pas : le fil est CALCULÉ (voir `thread.ts`), et
 * l'écran qui aurait oublié de lui passer les jours ou les typologies aurait compté un
 * fil DIFFÉRENT des six autres — sans planter. Le symptôme serait un « Retour » qui
 * saute une étape, ou un « Continuer » mort sur un écran qui en a pourtant une après
 * lui. Une seule lecture de l'offre de l'événement, ici, et les sept la partagent.
 *
 * ⚠️ LES RÉPONSES VOYAGENT AVEC. `go` les emporte systématiquement : un écran qui
 * naviguait sans état vidait tout ce qui avait été répondu dans ce parcours et n'était
 * pas encore écrit — les jours cochés, la commande, le profil. La leçon avait déjà été
 * payée au fil d'Ariane (voir `funnel/Steps.tsx`) ; la centraliser est ce qui évite de
 * la repayer au huitième écran.
 *
 * ⚠️ ET QUITTER LE PARCOURS SANS ELLES PRÉVIENT. Une correction emportée ici n'est écrite
 * qu'à la confirmation (ou par « Enregistrer », en page) ; le garde qui avertit avant de
 * la perdre est posé ici, une fois pour les sept écrans (voir `funnel/leaveGuard.ts`).
 */
export function useFunnelNav(path: string): {
  /** L'étape d'avant dans le fil, s'il y en a une. */
  previous: (Screen & { funnel: string }) | undefined;
  /** Celle d'après — `undefined` ferme « Continuer ». */
  next: (Screen & { funnel: string }) | undefined;
  /** Y aller, en emportant les réponses de ce parcours et le contexte de l'événement. */
  go: (path: string, extra?: Answers) => void;
  /** Une correction de cet écran attend d'être enregistrée (voir `useLeaveGuard`). */
  unsaved: boolean;
} {
  const { value } = useSettings();
  const event = useEvent();
  const navigate = useNavigate();
  const [answers] = useAnswers();
  const session = useSessionKey();
  const unsaved = useLeaveGuard();

  const offer = useOffer();
  const steps = activeSteps(value, offer);
  const here = steps.findIndex((s) => s.path === path);

  return {
    previous: here > 0 ? steps[here - 1] : undefined,
    next: stepAfter(value, path, offer),
    go: (toward, extra = {}) =>
      navigate(withContext(toward, event?.token), {
        // L'empreinte de la session voyage avec : voir `funnel/answerSession.ts`.
        state: stamped({ ...answers, ...extra }, session),
      }),
    unsaved,
  };
}
